/** Shared chrome for every form side panel; add something here only once a second panel needs it. */

import styled from "styled-components";

import { ANIMATION, SPACING, FONTS } from "../../utils/constants";
import { AmountVariant, amountVariantColor } from "../../utils/tone";
import { BaseButton } from "../Button";

/** Panel header (desktop): eyebrow + title stacked above the form. */
export const PanelHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.xs}px;
`;

export const HeaderEyebrow = styled.span`
  font-size: var(--text-xs);
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-primary);
`;

export const HeaderTitle = styled.h2`
  margin: 0;
  font-family: ${FONTS.serif};
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--color-text-default);
`;

/** Desktop form: vertical stack of bordered field cards. */
export const CardsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  width: min(500px, 100vw);
  color: var(--color-select-text);
`;

export const FormCard = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.md}px;
  padding: ${SPACING.md}px;
  border: 1px solid var(--color-border);
  border-radius: 12px;
  background: var(--color-card-background);
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TwoColumnRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${SPACING.md}px;
`;

export const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: ${SPACING.xs}px;
`;

export const FieldLabel = styled.label`
  display: block;
  font-size: var(--text-xs);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--color-text-secondary);
`;

export const FieldHelper = styled.span`
  display: block;
  font-size: var(--text-sm);
  font-style: italic;
  color: var(--color-text-secondary);
  margin-top: ${SPACING.xs}px;
`;

/** Small fixed-choice control (fee vs interest, deposit vs withdraw) with no colour of its own — direction comes from context. */
export const SegmentedControl = styled.div`
  display: flex;
  background: var(--color-background-0);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  padding: ${SPACING.xs}px;
  gap: ${SPACING.xs}px;
`;

export const Segment = styled(BaseButton)<{ $active: boolean }>`
  justify-content: center;
  flex: 1;
  border: none;
  border-radius: 9px;
  padding: ${SPACING.sm}px;
  cursor: pointer;
  font-family: ${FONTS.serif};
  font-size: var(--text-md);
  font-weight: 600;
  background: ${({ $active }) =>
    $active ? "var(--color-card-background)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--color-text-default)" : "var(--color-text-secondary)"};
  box-shadow: ${({ $active }) =>
    $active ? "0 1px 3px rgba(0, 0, 0, 0.12)" : "none"};
  transition: background ${ANIMATION.duration.fast} ${ANIMATION.easing.easeOut};
`;

export const ToneSegment = styled(Segment)<{ $tone?: AmountVariant }>`
  color: ${({ $active, $tone }) =>
    $active ? amountVariantColor($tone) : "var(--color-text-secondary)"};
`;

/** The field-level error, for a control that renders no input of its own to carry it. */
export const FieldError = styled.span`
  display: block;
  margin-top: ${SPACING.xs}px;
  color: var(--color-error);
  font-size: var(--text-xs);
`;

/** Panel-wide error banner for failures no single field owns (e.g. a rollup that couldn't value a position). */
export const PanelBanner = styled.div`
  padding: ${SPACING.sm}px ${SPACING.md}px;
  border: 1px solid var(--color-error-border);
  border-radius: 12px;
  background: var(--color-error-background);
  color: var(--color-error);
  font-size: var(--text-sm);
`;

export const FooterStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING.sm}px;
`;

export const FooterNote = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  color: var(--color-text-secondary);
`;

/** Footer action row (desktop): Cancel left, primary submit right. */
export const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: ${SPACING.md}px;

  button {
    min-height: 40px;
    min-width: 100px;
  }
`;

/** Centred wrapper for the loading / error body shown in place of the form. */
export const StateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.md}px;
  padding: ${SPACING.xl}px ${SPACING.md}px;
  text-align: center;
  color: var(--color-text-secondary);
`;
